import { emptyDocChatExpectations } from 'features/evaluations/agentic/emptyDocChatExpectations'
import type {
  Citation,
  SearchResultDoc,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { groupPageTextCitations } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/groupPageTextCitations'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'

/** Rolodex entry ids stop at 74 characters. `eval-case-` leaves 64 for the name. */
const MAX_CASE_NAME_LENGTH = 64
const NAME_PREFIX = 'docchat_'

const DOCCHAT_EVAL_CHANGE_NOTE = 'Captured from DocChat'

export interface DocChatCitedPage {
  documentId: string
  pageNum: number
}

export interface DocChatResponseCitations {
  conversation_id?: string
  response_segments?: Array<{ citations: Citation[] }>
  results?: SearchResultDoc[]
}

/** One document page on the draft case, with the quotes that cited it. */
export interface DocChatDraftCitation {
  documentId: string
  pageNum: number
  quotes: string[]
}

export function draftCitationKey(
  citation: Pick<DocChatDraftCitation, 'documentId' | 'pageNum'>,
  pipelineConfigId?: string
): string {
  const documentId = unscopeSearchDocumentIdForHydration(
    citation.documentId,
    pipelineConfigId
  )
  return `${documentId}:${citation.pageNum}`
}

/** The reply's saved citations, one per document page. */
export function draftCitationsFromResponse(
  response: DocChatResponseCitations | undefined
): DocChatDraftCitation[] {
  return groupPageTextCitations(savedPageCitations(response)).map(
    (citation) => ({
      documentId: citation.document_id,
      pageNum: citation.page_num,
      quotes: citation.quotes,
    })
  )
}

/** Citation rows for the draft, in the shape the citation chips render. */
export function draftCitationsToCitations(
  citations: DocChatDraftCitation[]
): Citation[] {
  return citations.flatMap((citation) => {
    const base = {
      type: 'page_text' as const,
      document_id: citation.documentId,
      page_id: `${citation.documentId}_pg_${citation.pageNum}`,
      page_num: citation.pageNum,
    }
    return citation.quotes.length
      ? citation.quotes.map((quote) => ({ ...base, quote }))
      : [{ ...base, quote: '' }]
  })
}

export type DocChatDocumentInput =
  | { kind: 'document'; documentId: string; pageNum?: number }
  | { kind: 'entry_id' }
  | { kind: 'no_document' }

const DOCUMENT_ID_PATTERN = /^DOC-[0-9a-f]{32}(?:_[0-9a-f]{12})?$/i
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Reads a pasted document id or document link. A link also gives the page
 * from `pageNumber`. Plain search text returns null.
 */
export function parseDocChatDocumentInput(
  value: string,
  pipelineConfigId?: string
): DocChatDocumentInput | null {
  const trimmed = value.trim()
  const isLink =
    /^https?:\/\//i.test(trimmed) || trimmed.includes('documentID=')
  let documentId = trimmed
  let pageNum: number | undefined
  if (isLink) {
    let params: URLSearchParams
    try {
      params = new URL(trimmed, 'https://forecast.invalid').searchParams
    } catch {
      return { kind: 'no_document' }
    }
    documentId = params.get('documentID')?.trim() ?? ''
    const page = Number(params.get('pageNumber')?.replaceAll('"', ''))
    if (Number.isInteger(page) && page >= 1) pageNum = page
  }
  if (UUID_PATTERN.test(documentId)) return { kind: 'entry_id' }
  if (!DOCUMENT_ID_PATTERN.test(documentId)) {
    return isLink ? { kind: 'no_document' } : null
  }
  return {
    kind: 'document',
    documentId: unscopeSearchDocumentIdForHydration(
      documentId,
      pipelineConfigId
    ),
    pageNum,
  }
}

interface DocChatResultPages {
  documentId: string
  pages: number[]
}

/** Documents the reply searched, with the pages the search matched. */
export function docChatResultPages(
  response: DocChatResponseCitations | undefined,
  pipelineConfigId?: string
): DocChatResultPages[] {
  const byDocument = new Map<string, Set<number>>()
  for (const result of response?.results ?? []) {
    const documentId = unscopeSearchDocumentIdForHydration(
      result.document_id,
      pipelineConfigId
    )
    const pages = byDocument.get(documentId) ?? new Set<number>()
    for (const highlight of result.highlights ?? []) {
      if (highlight.page_num >= 1) pages.add(highlight.page_num)
    }
    byDocument.set(documentId, pages)
  }
  return [...byDocument].map(([documentId, pages]) => ({
    documentId,
    pages: [...pages].sort((a, b) => a - b),
  }))
}

interface BuildDocChatEvalCaseInput {
  query: string
  replyText: string
  siteId: string
  orgId: string
  conversationId?: string | null
  mustState?: string[]
  mustNotState?: string[]
  citedPages: DocChatCitedPage[]
  pipelineConfigId?: string
  updatedBy: string
  capturedAt: string
  /** Short unique id so two similar questions do not share a name. */
  nameId: string
  /** Edited case name. Invalid characters are removed. */
  name?: string
}

function cleanText(value: string): string {
  return value.replaceAll('\u0000', '').trim()
}

/** Suggested name for a new DocChat case. */
export function docChatEvalCaseName(query: string, nameId: string): string {
  const id = nameId.toLowerCase().replace(/[^a-z0-9]/g, '') || 'case'
  const room = MAX_CASE_NAME_LENGTH - NAME_PREFIX.length - id.length - 1
  const slug = cleanText(query)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, Math.max(room, 1))
  return `${NAME_PREFIX}${slug || 'question'}_${id}`
}

/** Keeps the fixture charset `^[a-z0-9_-]+$` and the Rolodex name length. */
export function sanitizeDocChatEvalCaseName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, MAX_CASE_NAME_LENGTH)
}

function savedPageCitations(
  response: DocChatResponseCitations | undefined
): Citation[] {
  return (response?.response_segments ?? []).flatMap((segment) =>
    segment.citations.filter(
      (citation) => citation.type === 'page_text' && !citation.failure_reason
    )
  )
}

function applyCitedPages(
  expected: ReturnType<typeof emptyDocChatExpectations>,
  pages: DocChatCitedPage[],
  pipelineConfigId?: string
) {
  const sources: Array<{ document_id: string; pages: number[] }> = []
  for (const page of pages) {
    const documentId = unscopeSearchDocumentIdForHydration(
      page.documentId,
      pipelineConfigId
    ).trim()
    if (!documentId) continue
    const source = sources.find((item) => item.document_id === documentId)
    if (!source) {
      sources.push({ document_id: documentId, pages: [page.pageNum] })
    } else if (!source.pages.includes(page.pageNum)) {
      source.pages.push(page.pageNum)
    }
  }
  const documentIds = sources.map((source) => source.document_id)
  return {
    ...expected,
    retrieval: {
      ...expected.retrieval,
      references: {
        ...expected.retrieval.references,
        documents: {
          ...expected.retrieval.references.documents,
          must_include_ids: documentIds,
          min_count: documentIds.length > 0 ? 1 : 0,
        },
      },
      sources: { must_retrieve: sources },
    },
  }
}

/** A validated DocChat case. Answer checks come from the draft card. */
export function buildDocChatEvalCase(input: BuildDocChatEvalCaseInput) {
  const query = cleanText(input.query)
  const replyText = cleanText(input.replyText)
  const editedName = input.name ? sanitizeDocChatEvalCaseName(input.name) : ''
  const expected = applyCitedPages(
    emptyDocChatExpectations(input.siteId),
    input.citedPages,
    input.pipelineConfigId
  )
  const mustState = (input.mustState ?? [])
    .map((line) => line.trim())
    .filter(Boolean)
  const mustNotState = (input.mustNotState ?? [])
    .map((line) => line.trim())
    .filter(Boolean)
  return {
    name: editedName || docChatEvalCaseName(query, input.nameId),
    capability: 'fact_lookup' as const,
    query,
    context: {
      org_id: input.orgId,
      site_id: input.siteId,
      unit_id: null,
      failure_date: null,
    },
    golden_response: {
      text: replyText,
      provenance: {
        source: 'production_session',
        session_id: input.conversationId?.trim() || null,
        captured_at: input.capturedAt,
        answered_in_production: true,
        reviewed_by: null,
      },
    },
    expected: {
      ...expected,
      answer: {
        ...expected.answer,
        must_state: mustState,
        must_not_state: mustNotState,
      },
    },
    tags: [] as string[],
    archived: false,
    revision: 1,
    updated_by: input.updatedBy,
    change_note: DOCCHAT_EVAL_CHANGE_NOTE,
  }
}

export type DocChatEvalCase = ReturnType<typeof buildDocChatEvalCase>
